import moment from "moment/moment";
import {convertStringDateToDate} from './DateToolService';
import { map, forEach, filter, each, merge, some } from "lodash-es";

/**
 * Format the add allocation data.
 *
 * @returns {{}}}
 * @param accommodationSiteId
 * @param row
 */
export const addAllocationData = (accommodationSiteId, row) => {
    return {
        allocation: {
            from: convertStringDateToDate(row.from).format('Y-MM-DD'),
            to: convertStringDateToDate(row.to).format('Y-MM-DD'),
            type: row.type,
            units: parseInt(row.units) || null,
            releaseType: row.releaseType,
            releaseDays: parseInt(row.releaseDays) || null,
            releaseDate: (row.releaseDate) ? moment(row.releaseDate, 'DD/MM/YYYY').format('Y-MM-DD') : null
        }
    };
};

/**
 * Format the update allocation data.
 *
 * @returns {{} | null}
 * @param item
 * @param original
 */
export const updateAllocationData = (item, original) => {
    if (item) {
        return {
            ...item,
            from: convertStringDateToDate(item.from).format('Y-MM-DD'),
            to: convertStringDateToDate(item.to).format('Y-MM-DD'),
            type: item.type,
            releaseType: item.releaseType,
            units: calculateUnits(item, original),
            releaseDays: item.releaseDays || null,
            releaseDate: (item.releaseDate) ? convertStringDateToDate(item.releaseDate).format('Y-MM-DD') : null
        };
    }
    return null;
};

/**
 * Calculate the units count.
 *
 * @param item
 * @param original
 * @returns {null|number}
 */
const calculateUnits = (item, original) => {
    if (item.type !== 'ALLOTMENT') {
        return null
    } else {
        if (original.type !== 'ALLOTMENT') {
            return parseInt(item.units)
        } else {
            return parseInt(item.units) - parseInt(original.units)
        }
    }
};

/**
 * Format the block allocation data.
 *
 * @returns {{}}
 * @param item
 * @param comment
 */
export const blockAllocationData = (item, comment) => {
    let blockAllocation = {};
    if (item) {
        blockAllocation.from = convertStringDateToDate(item.from).format('Y-MM-DD');
        blockAllocation.to = convertStringDateToDate(item.to).format('Y-MM-DD');
        blockAllocation.comment = comment;
    }
    return blockAllocation;
};

/**
 * Format the request allocation data object.
 *
 * @returns {{}}
 * @param item
 * @param original
 */
export const requestAllocationData = (item, original) => {
    let requestAllocation = {};
    if(item && original) {
        requestAllocation = {
            ...item,
            from: convertStringDateToDate(item.from).format('Y-MM-DD'),
            to: convertStringDateToDate(item.to).format('Y-MM-DD'),
            units: parseInt(item.units) - parseInt(original.units) || null,
            releaseDate: (item.releaseDate) ? convertStringDateToDate(item.releaseDate).format('Y-MM-DD') : item.releaseDate,
            originalType: original.type,
            originalReleaseDays: original.releaseDays,
            originalReleaseDate: (original.releaseDate) ? convertStringDateToDate(original.releaseDate).format('Y-MM-DD') : original.releaseDate
        }
    }
    return requestAllocation;
};

/**
 * Modify allocation data.
 *
 * @param item
 * @param original
 * @returns {{units: *, releaseDate: null, releaseDays: null}}
 */
export const modifyAllocationData = (item, original) => {
    return {
        units: item.changes.deduction.units,
        releaseDate: original.changes.release.releaseDate ? convertStringDateToDate(item.changes.release.releaseDate).format('YYYY-MM-DD') : null,
        releaseDays: original.changes.release.releaseDays ? item.changes.release.releaseDays : null
    };
};

/**
 * Return a editUserData object consisting of the necessary information for the update user.
 *
 * @param user
 * @returns {{}}
 */
export const editUserData = (user) => {
    return {...{}, id: user.id, firstname: user.firstName, lastname: user.lastName, language: user.language, roleIds: map(user.roles, 'id')};
};

/**
 * Return a createUserData object consisting of the necessary information for the create user.
 *
 * @param user
 * @returns {{}}
 */
export const createUserData = (user) => {
    return {...{}, firstname: user.firstName, lastname: user.lastName, email: user.email, language: user.language, roleIds: map(user.roles, 'id')};
};

/**
 * Clones an object.
 *
 * @returns {{}}
 * @param object
 */
export const clone = (object) => {
    return {...{}, ...object};
};

/**
 * Clone a JSON object.
 *
 * @param json
 * @returns {any}
 * @constructor
 */
export const JSONClone = (json) => {
    return JSON.parse(JSON.stringify(json));
};

/**
 * Converts the string to a number if possible, else returns string.
 *
 * @param string
 * @returns {number | *}
 */
export const convertStringToNumber = (string) => {
    return parseInt(string) || string;
};

/**
 * Maps the bookings object to ensure that booking ref or supplier ref that is a number is converted
 * to be able to sort it correctly.
 *
 * @param bookings
 * @returns {Array}
 */
export const mapBookings = (bookings) => {
    let mappedBookings = [];
    forEach(bookings, (booking) => {
        let mappedBooking = {};
        forEach(booking, (value, key) => {
            if (key === 'bookingRef' || key === 'supplierRef') {
                mappedBooking[key] = parseInt(value) || value
            } else {
                mappedBooking[key] = value
            }
        });
        mappedBookings.push(mappedBooking)
    });
    return mappedBookings;
};

/**
 * Creates a days object which resets all the days not selected in the radio group.
 *
 * @param item
 * @returns {{}}}
 */
export const convertDaysObject = (item) => {
    const clone = {...{}, ...item};
    const reset = {
        days: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
        }
    };
    return {days: {...reset.days, ...clone.days}};
};

/**
 * Extract the route filter data from the sites object.
 *
 * @param sites
 * @param siteId
 * @param accommodationSiteId
 * @returns {{site: Array|undefined, accommodation: *}}
 */
export const getRouteFilterData = (sites, siteId, accommodationSiteId) => {
    const site = sites ? filter(sites, ['id', parseInt(siteId)])[0] : [];
    const accommodation = site ?
        filter(site.accommodations, ['accommodationSiteId', parseInt(accommodationSiteId)])[0]
        : null;
    return {site: site, accommodation: accommodation};
};

/**
 * Return the contract data for the specific contract id.
 *
 * @param items
 * @param contractId
 * @returns {*}
 */
export const getContractManagementData = (items, contractId) => {
    const contractData = filter(items, ['contractId', contractId])[0];
    return convertContractManagementData(contractData, contractId);
};

/**
 * Convert the contract data to map to view.
 *
 * @param data
 * @param contractId
 * @returns {{}}
 */
export const convertContractManagementData = (data, contractId) => {
    const basic = mapBasicContractData(data);
    const extended = mapExtendedContractData(data);
    return {
        basic: basic,
        extended: extended,
        contractId: contractId,
        readyForBackOffice: data.readyForBackOffice,
        readyForReview: data.readyForReview,
        supplierId: data.supplierId,
        supplierName: data.supplierName,
    };
};

/**
 * Convert the basic contract data.
 *
 * @param data
 * @returns {*}
 */
const mapBasicContractData = (data) => {
    return data ? {
        priceExtra: data.priceExtra,
        allotmentRules: data.allotmentRules,
        arrivalRules: data.arrivalRules,
        commission: data.commission,
        discounts: data.discounts,
        notes: data.notes
    } : null;
};

/**
 * Map the extended contract data.
 *
 * @param data
 * @returns {*}
 */
const mapExtendedContractData = (data) => {
    return data ? {
        accInfo: data.accommodations.length > 0 ? data.accommodations : [{
            entityId: undefined,
            name: undefined,
            accommodationId: undefined,
            text: undefined,
            files: [],
            images: [],
            s3: [],
            imagesS3: [],
            sites: []
        }],
        siteInfo: data.sites.length > 0 ? data.sites : [{
            showInContract: true,
            entityId: undefined,
            name: undefined,
            siteId: undefined,
            text:undefined,
            files: [],
            images: [],
            s3: [],
            imagesS3: []
        }],
        arrivalDeparture: data.arrivalDeparture,
        brands: data.brands
    } : null;
};

/**
 * Return progress object.
 *
 * @param data
 * @returns {{}}
 */
export const getContractManagementProgress = (data) => {
    return {
        basic: getBasicProgress(data.basic),
        extended: getExtendedProgress(data.extended)
    }
};

/**
 * Get the progress of the basic part.
 *
 * @param basicData
 * @returns {{priceExtra: boolean, allotmentRules: boolean, arrivalRules: boolean, commission: boolean, discounts: boolean, notes}}
 */
const getBasicProgress = (basicData) => {
    return {
        priceExtra: getDefaultProgress(basicData.priceExtra),
        allotmentRules: getDefaultProgress(basicData.allotmentRules),
        arrivalRules: getDefaultProgress(basicData.arrivalRules),
        commission: getDefaultProgress(basicData.commission),
        discounts: getDefaultProgress(basicData.discounts),
        notes: basicData.notes.text
    }
};

/**
 * Get the progress of the extended contract part.
 *
 * @param extendedData
 * @returns {{}}
 */
const getExtendedProgress = (extendedData) => {
    return {
        siteInfo: getComplexProgress(extendedData.siteInfo),
        accInfo: getComplexProgress(extendedData.accInfo),
        arrivalDeparture: getDefaultProgress(extendedData.arrivalDeparture),
        brands: extendedData.brands.length > 0
    };
};

/**
 * Check if either text is filled in or their is a file attached.
 *
 * @param data
 * @returns {*|boolean}
 */
const getDefaultProgress = (data) => {
    return data.text || data.s3.length > 0;
};

/**
 * Check if name is present along with either text or a file.
 *
 * @param items
 */
const getComplexProgress = (items) => {
    let valid = true;
    each(items, item => {
        if (!(item.name && (item.text || item.s3.length > 0))) valid = false;
    });
    return valid;
};

/**
 * Merge two items deep.
 *
 * @param itemA
 * @param itemB
 */
export const mergeDeep = (itemA, itemB) => {
    return merge(itemA, itemB);
};

/**
 * Generate form data object.
 *
 * @param form
 */
export const getFormSaveData = (form) => {
    let formData = new FormData();
    formData = getFormSaveDataBasic(form, formData);
    formData = getFormSaveDataExtended(form, formData);
    formData.append('brands', JSON.stringify(form.extended.brands));
    return formData;
};

/**
 * Get basic form data.
 *
 * @param form
 * @param formData
 */
export const getFormSaveDataBasic = (form, formData) => {
    formData = mapDefault(form.basic.allotmentRules, formData, 'allotmentRules');
    formData = mapDefault(form.basic.arrivalRules, formData, 'arrivalRules');
    formData = mapDefault(form.basic.commission, formData, 'commission');
    formData = mapDefault(form.basic.discounts, formData, 'discounts');
    formData = mapDefault(form.basic.notes, formData, 'notes');
    formData = mapDefault(form.basic.priceExtra, formData, 'priceExtra');
    return formData;
};

/**
 * Map default values to form data.
 *
 * @param data
 * @param formData
 * @param part
 */
const mapDefault = (data, formData, part) => {
    formData.append(part + 'Text', data.text || '');
    if (data.files) {
        each(data.files, (file, index) => {
            formData.append(part + 'S3_' + (index+1), file);
        })
    }
    return formData;
};

/**
 * Get extended form data.
 *
 * @param form
 * @param formData
 * @returns {FormData}
 */
export const getFormSaveDataExtended = (form, formData) => {
    formData = mapAccommodations(form.extended.accInfo, formData);
    formData = mapSites(form.extended.siteInfo, formData);
    formData = mapFiles(form.extended.siteInfo, formData, 'siteInfo');
    formData = mapFiles(form.extended.accInfo, formData, 'accInfo');
    formData = mapDefault(form.extended.arrivalDeparture, formData, 'arrivalDeparture');

    return formData;
};

/**
 * Map sites info and append to form.
 *
 * @param siteInfo
 * @param formData
 */
const mapSites = (siteInfo, formData) => {
    let fileIndex = 1;
    let imageIndex = 1;
    let sites = map(siteInfo, site => {
        let filesData = generateFileReferences(site.files, 'siteInfoS3', fileIndex);
        let imagesData = generateFileReferences(site.images, 'siteInfoImage', imageIndex);
        fileIndex = filesData.index;
        imageIndex = imagesData.index;
        return {
            showInContract: site.showInContract,
            entityId: site.entityId || null,
            name: site.name || null,
            id: site.id || null,
            text: site.text || null,
            files: filesData.files,
            images: imagesData.files
        }
    });
    formData.append('siteInfo', JSON.stringify({sites: validateDataArray(sites)}));
    return formData;
};

/**
 * Filter the data to ensure that only non default items a send.
 *
 * @returns {Array}
 * @param items
 */
const validateDataArray = (items) => {
    return filter(items, item => {
        const files = item.files.length > 0 ? item.files : null;
        const images = item.images.length > 0 ? item.images : null;
        return item.name || item.text || item.s3 || files || item.imagesS3 || images
    });
};

/**
 * Map accommodations info and append to form.
 *
 * @param accInfo
 * @param formData
 */
const mapAccommodations = (accInfo, formData) => {
    let fileIndex = 1;
    let imageIndex = 1;
    let accommodations = map(accInfo, accommodation => {
        let filesData = generateFileReferences(accommodation.files, 'accInfoS3', fileIndex);
        let imagesData = generateFileReferences(accommodation.images, 'accInfoImage', imageIndex);
        fileIndex = filesData.index;
        imageIndex = imagesData.index;
        return {
            entityId: accommodation.entityId || null,
            name: accommodation.name || null,
            id: accommodation.id || null,
            text: accommodation.text || null,
            files: filesData.files,
            images: imagesData.files,
            sites: accommodation.sites
        }
    });
    formData.append('accInfo', JSON.stringify({accommodations:  validateDataArray(accommodations)}));
    return formData;
};

/**
 * Map files and append to form.
 *
 * @param items
 * @param formData
 * @param part
 * @returns {*}
 */
const mapFiles = (items, formData, part) => {
    let s3index = 1;
    let imageIndex = 1;
    each(items, item => {
        for (let i = 0; i < item.files.length; i++) {
            formData.append(part + 'S3_' + (s3index++), item.files[i]);
        }
        for (let i = 0; i < item.images.length; i++) {
            formData.append(part + 'Image_' + (imageIndex++), item.images[i]);
        }
    });
    return formData;
};

/**
 * Extract file names.
 *
 * @param files
 * @param prefix
 * @param index
 * @returns {{files: Array, index: *}}
 */
const generateFileReferences = (files, prefix, index) => {
    let mappedFiles = map(files, () => {
        let mappedFile = prefix + '_' + index;
        index++;
        return mappedFile;
    });
    return {files: mappedFiles, index: index};
};

/**
 * Generate the download path.
 *
 * @param file
 * @param subPath
 * @returns {string}
 */
export const generateDownloadPath = (file) => {
    const baseUrl = process.env.GATEWAY_API_URL;
    return baseUrl + '/file/basiccontract' + file.substring(file.lastIndexOf('/'), file.length);
};

/**
 * Extract the file name.
 *
 * @param file
 */
export const extractFileName = (file) => {
    return typeof file === 'string' ? file.substring(file.lastIndexOf('/') + 1, file.length) : file.name;
};

/**
 * Extract file name and file extension.
 *
 * @param file
 * @returns {{name: string, extension: string}}
 */
export const extractFileAndExtension = (file) => {
    const fileName = extractFileName(file);
    const name = fileName.substring(0, fileName.lastIndexOf('.'));
    const extension = fileName.substring(fileName.lastIndexOf('.') +1, fileName.length);
    return {name: name, extension: extension};
};

/**
 * Get the extension from MIMEType.
 *
 * @param MIMEType
 * @returns {string}
 */
export const getExtensionFromMIMEType = (MIMEType) => {
    switch (MIMEType) {
    case 'application/pdf':
        return 'pdf';
    case 'application/vnd.ms-excel':
        return 'xls';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
    case 'application/msword':
        return 'doc';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
    case 'application/vnd.ms-powerpoint':
        return 'ppt';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        return 'pptx';
    default:
        return 'txt';
    }
};

/**
 * Merge array with fileList.
 *
 * @param files
 * @param fileList
 */
export const mergeFileLists = (files, fileList) => {
    let fileArray = Array.prototype.slice.call(fileList);
    return files.concat(fileArray);
};

/**
 * Generate image url.
 *
 * @param file
 * @returns {*}
 */
export const generateS3ImageDownloadPath = (file) => {
    return process.env.AWS_BUCKET_IMAGE_URL + file
};

/**
 * Get the Twenty link for each sub part of the contract management.
 *
 * @returns {*}
 * @param subPart
 */
export const getContractManagementPartLink = (subPart) => {
    const baseUrl = process.env.BACKEND_API;
    if (subPart) {
        switch (subPart) {
        case 'priceExtra':
            return [
                baseUrl + '/support-system/allocation',
                baseUrl + '/support-system/extra'
            ];
        case 'allotmentRules':
            return baseUrl + '/support-system/allocation';
        case 'arrivalRules':
            return baseUrl + '/support-system/arrival-rules';
        case 'commission':
            return baseUrl + '/support-system/allocation';
        case 'discounts':
            return baseUrl + '/support-system/discount';
        case 'siteInfo':
            return baseUrl + '/production/products/sites';
        case 'accInfo':
            return baseUrl + '/production/products/accommodations';
        case 'arrivalDeparture':
            return baseUrl + '/support-system/arrival-rules';
        }
    }
    return null;
};

/**
 * Convert the contract form data to an array.
 *
 * @param form
 * @returns {Array}
 */
export const getContractFormData = (form) => {
    let data = [];
    let id = 1;
    for (let attribute in form) {
        data.push({...form[attribute], field: id++});
    }
    return data;
};

/**
 * Extract path and filename from file.
 *
 * @param file
 * @returns {{path: string, name: string}}
 */
export const extractPathAndName = (file) => {
    return {
        path: file.substring(0, file.lastIndexOf('/')),
        name: file.substring(file.lastIndexOf('/') + 1, file.lastIndexOf('.')),
        type: file.substring(file.lastIndexOf('.') + 1, file.length)
    };
};

/**
 * Get the current contract year.
 *
 * @returns {*}
 */
export const getCurrentContractYear = () => {
    const date = moment();
    if (date.month() >= 7) {
        return (date.year() + 1) + '';
    }
    return date.year()  + '';
};

/**
 * Map the contract accepted parts
 * @param payload
 */
export const mapContractAcceptedProgressParts = (payload, progressObj) => {
    let acceptedObj = clone(progressObj);

    each(payload, (item) => {
        acceptedObj[progressPartList[item.field - 1]] = true;
    });
    return acceptedObj;
};

/**
 * List of contract part types.
 *
 * @type {string[]}
 */
export const progressPartList = [
    'supplierInfo',
    'sites',
    'accommodations',
    'prices',
    'extras',
    'arrivalRules',
    'cancellationRules',
    'discounts',
    'allocations',
    'terms'
];

/**
 * Get the first product type from suppliersProductsObject.
 *
 * @param suppliersProductsObject
 * @returns {{supplier: *, productType: string}}
 */
export const getProductTypesAndSuppliersFromSuppliersProductsObject = (suppliersProductsObject) => {
    const productTypes = Object.keys(suppliersProductsObject).sort();
    const productType = checkIfCampingIsAmongTheProductTypes(productTypes) ? 'Camping' : productTypes[0];
    const supplier = suppliersProductsObject[productType][0];
    return {
        productTypes,
        productType,
        supplier
    };
};

/**
 * Check if camping is one of the product types.
 *
 * @param productTypes
 * @returns {boolean}
 */
const checkIfCampingIsAmongTheProductTypes = (productTypes) => {
    return some(productTypes, (productType) =>  {
        return productType === 'Camping';
    })
};



// WEBPACK FOOTER //
// ./src/services/ConverterService.js